import { Component, OnInit, ElementRef, ViewChild, ViewChildren, QueryList, ChangeDetectorRef } from '@angular/core';
import { PageBase, UIService, RentalInfo, CacheService, PhotoAngle, PhotoAngleInfo } from 't4core';
import { NavigationService } from 'app-components';
import { AppIntegrationService } from 't4-app-integration';
import { Router, ActivatedRoute } from '@angular/router';
import { ActiveRentalService } from 'app-components';
import { LayoutService } from 'app-components';
import { MatStepper } from '@angular/material/stepper';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { PhotoViewComponent } from '../../Components/photo-view/photo-view.component';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { eSeverityLevel } from '@microsoft/applicationinsights-web';

export class PhotoModel {
  public angle: PhotoAngleInfo;
  public photoUri: string;
  public data?: string;
  public taken?: boolean;
  public control: FormControl;
}

@Component({
    selector: 'app-take-pictures',
    templateUrl: './take-pictures.component.html',
    styleUrls: ['./take-pictures.component.css'],
    providers: [{
            provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false }
        }],
    standalone: false
})                    
export class TakePicturesComponent extends PageBase implements OnInit {

  @ViewChildren(PhotoViewComponent) appPhotoViewComponents: QueryList<PhotoViewComponent>;
  @ViewChild('stepper', { static: false }) stepper: MatStepper;
  public currentStep: number = 0;

  public ignoreCameraCheck: boolean = false;  
  public permissionAsked: boolean = false; 
   

  public event: string = "Pickup"; 
  public booking: RentalInfo;

  public photosTaken: number = 0;
  public photos: PhotoModel[] = []; 

  public additionalPhotosArray: PhotoModel[] = [];

  public isSummaryStep: boolean = false;

  public fallbackImage: string = "/Images/fallback-image-photo.png";

  constructor(el: ElementRef, private router: Router, private aviationService: NavigationService, public appService: AppIntegrationService, private ui: UIService, private rentalService: ActiveRentalService, private route: ActivatedRoute, private layoutService: LayoutService, private formBuilder: FormBuilder) {
    super(el);
    this.appService.requestDeviceState();

    this.event = this.aviationService.getParam("event");

    this.additionalPhotosArray.push({ angle: null, photoUri: null, control: new FormControl('') });
  }

  async ngOnInit() {
    this.logDiagnostic('Initialization started');
    try {
      await this.initializePhotos();
    } catch (error) {
      this.logDiagnostic('Initialization failed', {}, true, error);
      throw error;
    }
  }

  private logDiagnostic(action: string, properties: { [key: string]: string } = {}, failed: boolean = false, error?: any) {
    try {
      this.Insights.trackDetailedTrace('TakePictures: ' + action,
        failed ? eSeverityLevel.Error : eSeverityLevel.Information, '', {
          rentalId: String(this.booking?.Id ?? ''),
          photoEvent: this.event,
          stepIndex: String(this.currentStep),
          photoCount: String(this.photos.length),
          photosTaken: String(this.photosTaken),
          additionalPhotoCount: String(this.additionalPhotosArray.filter(x => x.taken || x.photoUri).length),
          cameraPermission: String(this.appService.Device?.Camera),
          online: String(navigator.onLine),
          ...properties,
          errorName: typeof error?.name === 'string' ? error.name : '',
          httpStatus: typeof error?.status === 'number' ? String(error.status) : ''
        });
    } catch {
      // Diagnostics must not interrupt the photo workflow.
    }
  }

  private async initializePhotos() {
    this.booking = await this.rentalService.getBooking();
    this.logDiagnostic('Booking loaded');

    this.Api.post<boolean>("/Rental/LogEvent", "Start taking pictures", { rentalId: this.booking.Id });

    // Load cached data
    var tmp = this.rentalService.getCached("RP_photoList");
    if (tmp) {
      this.photos = tmp.data;
    }

    tmp = this.rentalService.getCached("RP_addPhotoList");
    if (tmp) {
      this.additionalPhotosArray = tmp.data;
    } 

    this.logDiagnostic('Cached photos restored');

    if (this.photos.length <= 0) {
      var token = this.UI.beginLoading("Intellitrailer.PhotoAngle.LoadingAnglesMsg", "Loading Angles", null);
      
      if (this.event != "Pickup") {

   

        // Type - PhotoAngleInfo
        this.logDiagnostic('Loading photo angles');
        var angles = await this.rentalService.getPhotoAngles();
        this.logDiagnostic('Photo angles loaded', { angleCount: String(angles.length) });
        angles.map(x => this.photos.push({ angle: x, photoUri: '', control: new FormControl('') }));
        this.Api.post<boolean>("/Rental/LogEvent", "Found angles: " + angles.length, { rentalId: this.booking.Id });
        //Updated the values and settings a "required" condition.
        this.photos.map(x => x.control.setErrors({ "required": true }));
      }

      this.rentalService.storeWithRental("RP_photoList", this.photos, new Date(new Date().getTime() + 10 * 60 * 1000));
      this.rentalService.storeWithRental("RP_addPhotoList", this.additionalPhotosArray, new Date(new Date().getTime() + 10 * 60 * 1000));
      

      this.UI.loaderCompleted(token);
    }

    // Move to current photo
    setTimeout(async () => {
      try {
      var i = 0;
      var stopped = false;
      for (var item of this.photos) {
        item.control = new FormControl('');

        if (item.photoUri || item.taken) {
          if (!stopped) {
            this.stepper._steps.get(i)._markAsInteracted();
            this.photoTaken(item);
          }

          item.control.reset();
        } else {
          stopped = true;
          item.control.setErrors({ "required": true });
        }
      }

      for (var item of this.additionalPhotosArray) {
        item.control = new FormControl('');

        if (item.photoUri || item.taken) {
          item.control.reset();
        } else {
          item.control.setErrors({ "required": true });
        }
      }

      // Anything in pipeline?
      setTimeout(() => this.checkForPending(), 1000);

      this.logDiagnostic('Stepper restored', { stepperAvailable: String(!!this.stepper) });
      } catch (error) {
        this.logDiagnostic('Stepper restoration failed', { stepperAvailable: String(!!this.stepper) }, true, error);
      }
    }, 100);
  }

  private async checkForPending(): Promise<Boolean> {
    this.logDiagnostic('Pending photo check started');
    try {
    var photo = this.appService.findPendingPhoto();
    if (photo) {
      this.logDiagnostic('Pending photo found', { hasImage: String(!!photo.Image) });
      var angle = null;

      for (var a of this.photos) {
        if (!a.photoUri) {
          angle = a;
          break;
        }
      }

      if (angle) {
        this.logDiagnostic('Pending photo upload started', { angleId: String(angle.angle.Id) });
        angle.photoUri = await this.Api.post<string>("Data/Store" + this.event + "Photo", "\"" + photo.Image + "\"", { angleId: angle.angle.Id, rentalId: this.booking.Id });
        this.logDiagnostic('Pending photo upload finished', { angleId: String(angle.angle.Id), hasStoredUri: String(!!angle.photoUri) }, !angle.photoUri);
        angle.data = photo.Image;
        angle.taken = true;
        this.photoTaken(angle);

        return true;
      } else if (this.additionalPhotosArray) {
        angle = this.additionalPhotosArray[this.additionalPhotosArray.length - 1];
        this.logDiagnostic('Pending photo upload started', { angleId: '0' });
        angle.photoUri = await this.Api.post<string>("Data/Store" + this.event + "Photo", "\"" + photo.Image + "\"", { angleId: 0, rentalId: this.booking.Id });
        this.logDiagnostic('Pending photo upload finished', { angleId: '0', hasStoredUri: String(!!angle.photoUri) }, !angle.photoUri);
        angle.data = photo.Image;
        this.additionalPhotoTaken(this.additionalPhotosArray.length - 1);

        return true;
      }
    } else if (!this.rentalService.secretSafetyCheckFlag) {
      this.logDiagnostic('App restarted; pending photo missing', {}, true);
    } else {
      this.logDiagnostic('No pending photo');
    }
    } catch (error) {
      this.logDiagnostic('Pending photo recovery failed', {}, true, error);
    }

    return false;
  }
 
  ngAfterViewInit() {
  }

   
  onSelectionChange(event: StepperSelectionEvent) {
    this.logDiagnostic('Step changed', { previousStep: String(event.previouslySelectedIndex), selectedStep: String(event.selectedIndex) });
    // If user navigates away from a step that had an open camera, ensure we close it.
    try {
      const prevIndex = event?.previouslySelectedIndex;
      if (typeof prevIndex === 'number' && prevIndex >= 0 && prevIndex < this.photos.length && this.appPhotoViewComponents && this.appPhotoViewComponents.length > 0) {
        const prevPhoto = this.photos[prevIndex];
        if (prevPhoto) {
          const prevChild = this.appPhotoViewComponents.find(x => x.photo === prevPhoto);
          if (prevChild && prevChild.cameraOpen) {
            prevChild.cancelBrowserCamera();
          }
        }
      }
    } catch (error) {
      this.logDiagnostic('Previous camera cleanup failed', {}, true, error);
    }

    //Controlls the Confirm/Submit button, as it should only show when get to the last step (summary)
    if (event.selectedIndex === this.photos.length) {
      this.isSummaryStep = true;
    } else {
      this.isSummaryStep = false;
    }
  }

  public async confirm() {
    this.logDiagnostic('Photos confirmed');
    // TODO Validate pictures
    this.Api.post<boolean>("/Rental/LogEvent", "Photos confirmed", { rentalId: this.booking.Id });

    //Fault report
    if (this.event == "FaultReport")
      this.UI.alert("Intellitrailer.FaultReport.SuccessHeadLine", "Fault report sent", "Intellitrailer.FaultReport.SuccessMessage", "The fault report has been sent. We thank you for contributing.");

    this.aviationService.executeCommand("Confirm");
  }

  public photoTaken(photo: PhotoModel) {
    this.logDiagnostic('Photo completion received', { angleId: String(photo.angle?.Id ?? 0) });
    this.Api.post<boolean>("/Rental/LogEvent", "Photo taken: " + photo.angle.Name, { rentalId: this.booking.Id });
    this.photosTaken++;

    if (this.event != "Pickup") {
      photo.control.reset();
      this.stepper.next();
    }

    this.rentalService.storeWithRental("RP_photoList", this.photos, new Date(new Date().getTime() + 10 * 60 * 1000));
  }

  public photoFailed() {
    this.logDiagnostic('Photo failure received', {}, true);
    this.ignoreCameraCheck = false;
  }


  public photoDeleted(photo: PhotoModel) {
    this.logDiagnostic('Photo deleted', { angleId: String(photo.angle?.Id ?? 0) });
    this.photosTaken--;

    //If the Index does exist, we remove it and updated the formControl.
    photo.control.setErrors({ "required": true })

    this.rentalService.storeWithRental("RP_photoList", this.photos, new Date(new Date().getTime() + 10 * 60 * 1000));
  }

 
  public additionalPhotoTaken(index: number) {
    this.logDiagnostic('Additional photo completion received');
    this.additionalPhotosArray.push({ angle: null, photoUri: null, control: new FormControl('') });

    this.rentalService.storeWithRental("RP_addPhotoList", this.additionalPhotosArray, new Date(new Date().getTime() + 10 * 60 * 1000));
  }

  public additionalPhotoDeleted(photo: PhotoModel) {
    this.logDiagnostic('Additional photo deleted', { photoIndex: String(this.additionalPhotosArray.indexOf(photo)) });
    this.additionalPhotosArray.splice(this.additionalPhotosArray.indexOf(photo), 1);

    this.rentalService.storeWithRental("RP_addPhotoList", this.additionalPhotosArray, new Date(new Date().getTime() + 10 * 60 * 1000));
  }

  // Takes Photo, when pressing the "Take Photo" Button.
  // Work around to instead have it inside the mat-stepper, loading issues.
    public async simulateChildClick() {
        if (this.appService.Device.Camera < 2) {
            this.permissionAsked = true;
        }

    this.logDiagnostic('Camera action requested');
    var currentPhoto = this.photos.find(x => x.control == this.stepper?.selected?.stepControl)
    const selectedAppPhotoView = this.appPhotoViewComponents.find(x => x.photo == currentPhoto);

    if (selectedAppPhotoView) {
      try {
        // If camera is not started, start it. If already started, take picture.
        if (!selectedAppPhotoView.cameraOpen) {
          await selectedAppPhotoView.startBrowserCamera();
        } else {
          await selectedAppPhotoView.captureAndUpload();
        }
      } catch (ex) {
        this.logDiagnostic('Camera action failed', { angleId: String(currentPhoto?.angle?.Id ?? 0) }, true, ex);
        // If something goes wrong, notify parent view
        this.photoFailed();
      }
    } else {
      this.logDiagnostic('Camera component unavailable', { hasCurrentPhoto: String(!!currentPhoto) }, true);
    }
  }

  // Getter used by template to decide label for the action button
  public get currentChildCameraOpen(): boolean {
    try {
      const currentPhoto = this.photos.find(x => x.control == this.stepper?.selected?.stepControl);
      if (!currentPhoto || !this.appPhotoViewComponents) return false;
      const selectedAppPhotoView = this.appPhotoViewComponents.find(x => x.photo == currentPhoto);
      return !!selectedAppPhotoView?.cameraOpen;
    } catch {
      return false;
    }
  }

  public goToStep(index: PhotoModel) {
    this.currentStep = this.photos.indexOf(index);
    this.logDiagnostic('Photo selected from summary');
  }

  public skipCameraCheck() {
    this.logDiagnostic('Camera permission check bypassed');
    this.ignoreCameraCheck = true;
  }
  public checkCameraPermission() {
    this.logDiagnostic('Camera permission checked');
    if (this.appService.Device.Camera < 2) {
      this.permissionAsked = true;
    }
  }

  public skipTakePictures() {
    this.logDiagnostic('Skip pictures requested', { allowed: String(this.appService.Device.Camera < 2) });
    if (this.appService.Device.Camera < 2) {
      this.aviationService.executeCommand("Skip");
    }
  }
 
}
